import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';

import { type CategoryNode } from '../../category-tree';

/**
 * 학기 선택 관리 hook
 *
 * 선택된 학년 노드(parentNode)를 받아서 학기 레벨을 관리합니다.
 * 이것이 교육 정보 계층의 마지막 레벨이므로 selectedNode를 반환하지 않습니다.
 */
export const useSelectSemester = ({
  initialSemesterId,
  parentNode,
}: {
  initialSemesterId?: string;
  parentNode?: CategoryNode;
}) => {
  const [selectedSemesterNode, setSelectedSemesterNode] = useState<
    CategoryNode | undefined
  >(() => parentNode?.subNodes.find(node => node.nodeId === initialSemesterId));
  const [error, setError] = useState<string>();
  const { t } = useTranslation(['build', 'common']);

  // 학기 노드들 (parentNode의 children)
  const semesterNodes: CategoryNode[] | undefined = useMemo(() => {
    if (!parentNode) return undefined;
    return parentNode.subNodes;
  }, [parentNode]);

  // 검증
  const validate = useCallback((): boolean => {
    if (semesterNodes && semesterNodes.length > 0 && !selectedSemesterNode) {
      setError(t('build:schoolInfo.requiredField'));
      return false;
    }
    setError(undefined);
    return true;
  }, [semesterNodes, selectedSemesterNode, t]);

  // 에러 클리어
  const clearError = useCallback(() => {
    setError(undefined);
  }, []);

  return {
    semesterNodes,
    selectedSemesterNode,
    setSelectedSemesterNode,
    error,
    validate,
    clearError,
  };
};
